import React, { useEffect, useState, useMemo } from "react";
import { ShoppingBag, Download, Package, BookOpen } from "lucide-react";
import { api, formatZAR, resolveUrl } from "../lib/api";
import { submitPayFast } from "../lib/payfast";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "All" },
  { key: "digital_music", label: "Digital Music", icon: Download },
  { key: "merchandise", label: "Merchandise", icon: Package },
  { key: "book", label: "Books & Resources", icon: BookOpen },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (tab === "all" ? products : products.filter((p) => p.type === tab)),
    [products, tab]
  );

  const buy = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post("/payments/payfast/product", {
        product_id: p.id,
        origin_url: window.location.origin,
      });
      submitPayFast(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div>
      <section className="bg-[#1F3864] text-white py-24 sm:py-28" data-testid="shop-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">The Shop</div>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95]">
            Wear it. Play it. <br /><span className="italic text-[#C00000]">Carry the message.</span>
          </h1>
        </div>
      </section>

      <section className="py-12 border-b border-gray-200 sticky top-20 bg-white z-20" data-testid="shop-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                tab === t.key ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700 hover:border-[#1F3864]"
              }`}
              data-testid={`shop-tab-${t.key}`}
            >
              {t.icon && <t.icon className="w-3.5 h-3.5 inline mr-2" />}
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="shop-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger">
          {filtered.map((p) => (
            <div key={p.id} className="group border border-gray-200 hover:border-[#1F3864] bg-white transition-colors" data-testid={`product-card-${p.id}`}>
              <div className="aspect-square overflow-hidden bg-gray-100 relative">
                <img src={resolveUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {p.type === "digital_music" && (
                  <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#C00000] text-white text-[10px] tracking-[0.2em] uppercase">Digital</div>
                )}
              </div>
              <div className="p-5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C00000] mb-2">
                  {p.type.replace("_", " ")}
                </div>
                <h3 className="font-display text-lg text-[#1F3864] mb-2 leading-snug line-clamp-2 min-h-[3rem]">{p.name}</h3>
                <p className="text-xs text-gray-500 line-clamp-2 mb-4 min-h-[2rem]">{p.description}</p>
                <div className="flex items-center justify-between">
                  <div className="font-display text-xl text-[#1F3864]">{formatZAR(p.price)}</div>
                  <button
                    onClick={() => buy(p)}
                    disabled={busy === p.id}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1F3864] hover:bg-[#162846] text-white text-xs tracking-wider uppercase transition-colors disabled:opacity-60"
                    data-testid={`action-buy-product-${p.id}`}
                  >
                    <ShoppingBag className="w-3.5 h-3.5" /> {busy === p.id ? "..." : "Buy"}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-20 text-center text-gray-500">No products in this category.</div>
          )}
        </div>
      </section>
    </div>
  );
}
